/**
 * NotificationPreferences.jsx
 * Modal for users to manage their notification preferences per category.
 *
 * Channels: in_app, email — each can be toggled independently.
 * Non-mutable categories (system/security) have toggles forced ON and disabled.
 */
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Modal,
  Table,
  Switch,
  Spin,
  Empty,
  Message,
  Typography,
  Tooltip,
} from '@arco-design/web-react';
import { IconLock } from '@arco-design/web-react/icon';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/apiClient';
import { API_ENDPOINTS } from '@/config/apiEndpoints';

const { Text } = Typography;

const NotificationPreferences = ({ visible, onClose }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: async () => {
      const res = await apiClient.get(API_ENDPOINTS.NOTIFICATIONS_PREFERENCES);
      return res.data;
    },
    enabled: visible,
  });

  const updateMutation = useMutation({
    mutationFn: (prefs) =>
      apiClient.put(API_ENDPOINTS.NOTIFICATIONS_PREFERENCES, {}, prefs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications', 'preferences'] });
      Message.success(t('notifications.preferences.saved'));
    },
    onError: () => {
      Message.error(t('notifications.preferences.saveError'));
    },
  });

  const items = useMemo(() => data?.items || [], [data]);

  const handleToggle = (category, field, value) => {
    const item = items.find((i) => i.category === category);
    if (!item) return;

    // Build update — if user turns off both channels, that's effectively "muted"
    const updated = {
      category,
      in_app: field === 'in_app' ? value : item.in_app,
      email: field === 'email' ? value : item.email,
      is_muted: false, // muted is derived from both channels being off
    };

    // If both channels are off, mark as muted
    if (!updated.in_app && !updated.email) {
      updated.is_muted = true;
    }

    updateMutation.mutate([updated]);
  };

  const columns = [
    {
      title: t('notifications.preferences.category'),
      dataIndex: 'label',
      width: 160,
      render: (label, record) => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Text bold>{label}</Text>
          {!record.is_mutable && (
            <Tooltip content={t('notifications.preferences.nonMutableHint')}>
              <IconLock style={{ color: 'var(--color-text-3)', fontSize: 12 }} />
            </Tooltip>
          )}
        </span>
      ),
    },
    {
      title: t('notifications.preferences.inApp'),
      dataIndex: 'in_app',
      width: 100,
      align: 'center',
      render: (v, record) => (
        <Tooltip
          content={!record.is_mutable ? t('notifications.preferences.cannotDisable') : ''}
          disabled={record.is_mutable}
        >
          <Switch
            checked={!record.is_mutable ? true : v}
            size="small"
            disabled={!record.is_mutable}
            onChange={(val) => handleToggle(record.category, 'in_app', val)}
          />
        </Tooltip>
      ),
    },
    {
      title: t('notifications.preferences.email'),
      dataIndex: 'email',
      width: 100,
      align: 'center',
      render: (v, record) => (
        <Tooltip
          content={!record.is_mutable ? t('notifications.preferences.cannotDisable') : ''}
          disabled={record.is_mutable}
        >
          <Switch
            checked={!record.is_mutable ? true : v}
            size="small"
            disabled={!record.is_mutable}
            onChange={(val) => handleToggle(record.category, 'email', val)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Modal
      title={t('notifications.preferences.title')}
      visible={visible}
      onCancel={onClose}
      footer={null}
      style={{ width: 480 }}
      unmountOnExit
    >
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Spin size={24} />
        </div>
      ) : items.length === 0 ? (
        <Empty description={t('notifications.preferences.noCategories')} />
      ) : (
        <Table
          columns={columns}
          data={items}
          rowKey="category"
          pagination={false}
          border={false}
          size="small"
          style={{ marginTop: 8 }}
        />
      )}
    </Modal>
  );
};

export default NotificationPreferences;
